import { openDatabase, saveSnapshotDatabase } from "../Utils/db";
import type { ITriggerRule, TriggerEvent } from "../interfaces/Widgets";
import { WidgetsType } from "../interfaces/Widgets";
import type Radio from "./Radio";
import type {
  TriggerHandler,
  TriggerActionType,
  TriggerUnsubscribe,
  TriggerTimerState,
  TriggerEventContext,
  EventSourceResult,
} from "./TriggerEngine.interfaces";
import { actionRegistry } from "./triggers";
import { matchesChatEvent } from "./triggers/chatMatch";
import { ruleIdentity } from "./triggers/ruleIdentity";
import { EVENT_SOURCE_MAP, type EventSourceWireFn } from "./triggers/events";

interface QueuedEvent {
  event: TriggerEvent;
  payload?: unknown;
  ancestors: Set<string>;
  target?: { ruleId: string; identity: string; isCurrent: () => boolean };
  resolve: () => void;
}

const OBSERVED_EVENTS: TriggerEvent[] = [
  "stream_started", "scene_switched", "playlist_started", "mic_muted",
];

class TriggerEngine {
  Radio: Radio;
  private wiredSources = new Map<EventSourceWireFn, EventSourceResult>();
  private destroyed = false;
  private activated = false;
  private ready = false;
  private activationWork?: Promise<void>;
  private syncWork: Promise<void> = Promise.resolve();
  private queue: QueuedEvent[] = [];
  private draining = false;
  private timerCommands = new Map<string, number>();
  private consumedOnce = new Set<string>();
  private suppressedEvents = new Map<string, ReturnType<typeof setTimeout>>();
  private actionHandlers = new Map<TriggerActionType, Set<TriggerHandler>>();
  private globalEventHandlers = new Set<(event: string, payload?: unknown) => void>();

  constructor(radio: Radio) {
    this.Radio = radio;
  }

  /** Radio calls this only after devices, scenes and panel storage are ready. */
  activate(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    if (this.activationWork) return this.activationWork;
    this.activated = true;
    this.activationWork = this.sync().then(() => {
      if (this.destroyed) return;
      this.ready = true;
      void this.drain();
    }).catch((error) => {
      this.activated = false;
      this.activationWork = undefined;
      throw error;
    });
    return this.activationWork;
  }

  sync(): Promise<void> {
    if (!this.activated || this.destroyed) return Promise.resolve();
    const work = this.syncWork.then(() => this.reconcileSources());
    this.syncWork = work.catch((error) => {
      console.error("[TriggerEngine] sync failed:", error);
    });
    return work;
  }

  private async reconcileSources() {
    if (this.destroyed) return;
    const rules = await this.getRules();
    if (this.destroyed) return;
    const enabledEvents = new Set(rules.filter((rule) => rule.enabled).map((rule) => rule.event));
    if (this.globalEventHandlers.size > 0) {
      for (const event of OBSERVED_EVENTS) enabledEvents.add(event);
    }
    const neededSources = new Set<EventSourceWireFn>();
    for (const event of enabledEvents) {
      const source = EVENT_SOURCE_MAP[event];
      if (source) neededSources.add(source);
    }
    for (const [source, result] of this.wiredSources) {
      if (neededSources.has(source)) continue;
      this.disposeSource(result);
      this.wiredSources.delete(source);
    }
    for (const source of neededSources) {
      let result = this.wiredSources.get(source);
      if (!result) {
        result = await source(this);
        if (this.destroyed) {
          this.disposeSource(result);
          return;
        }
        this.wiredSources.set(source, result);
      }
      await result.sync?.(rules);
      if (this.destroyed) return;
    }
  }

  private disposeSource(result: EventSourceResult) {
    for (const unsubscribe of result.unsubscribers) unsubscribe();
    if (result.timers) {
      for (const timer of result.timers.values()) clearTimeout(timer);
      result.timers.clear();
    }
  }

  destroy() {
    this.destroyed = true;
    this.ready = false;
    for (const result of this.wiredSources.values()) this.disposeSource(result);
    this.wiredSources.clear();
    for (const timer of this.suppressedEvents.values()) clearTimeout(timer);
    this.suppressedEvents.clear();
    for (const item of this.queue.splice(0)) item.resolve();
    this.actionHandlers.clear();
    this.globalEventHandlers.clear();
    this.consumedOnce.clear();
    this.timerCommands.clear();
  }

  subscribeAll(handler: (event: string, payload?: unknown) => void): TriggerUnsubscribe {
    if (this.destroyed) return () => {};
    this.globalEventHandlers.add(handler);
    void this.sync().catch(() => {});
    return () => {
      this.globalEventHandlers.delete(handler);
      void this.sync().catch(() => {});
    };
  }

  dispatchExternal(event: TriggerEvent, payload?: unknown): void {
    void this.enqueue(event, payload, new Set());
  }

  onAction(action: string, handler: TriggerHandler): TriggerUnsubscribe {
    if (this.destroyed) return () => {};
    let handlers = this.actionHandlers.get(action);
    if (!handlers) this.actionHandlers.set(action, handlers = new Set());
    handlers.add(handler);
    return () => { handlers.delete(handler); };
  }

  emitAction(action: string, payload?: unknown) {
    if (this.destroyed) return;
    for (const handler of this.actionHandlers.get(action) ?? []) {
      try {
        handler(payload);
      } catch (error) {
        console.error(`[TriggerEngine] Action observer failed: ${action}`, error);
      }
    }
  }

  suppressEvent(event: string, ms: number) {
    if (this.destroyed) return;
    clearTimeout(this.suppressedEvents.get(event));
    this.suppressedEvents.set(event, setTimeout(() => {
      this.suppressedEvents.delete(event);
    }, ms));
  }

  fireEvent(
    event: TriggerEvent,
    payload?: Record<string, string>,
    eventContext?: TriggerEventContext,
  ): Promise<void> {
    return this.enqueue(event, payload, new Set(eventContext?.ancestors));
  }

  /** Revalidate a due timer against the persisted rule and its current run. */
  fireRule(rule: ITriggerRule, isCurrent: () => boolean = () => true): Promise<void> {
    return this.enqueue(rule.event, { ruleId: rule.id }, new Set(), {
      ruleId: rule.id, identity: ruleIdentity(rule), isCurrent,
    });
  }

  private enqueue(
    event: TriggerEvent,
    payload: unknown,
    ancestors: Set<string>,
    target?: QueuedEvent["target"],
  ): Promise<void> {
    if (this.destroyed || this.suppressedEvents.has(event)) return Promise.resolve();
    return new Promise((resolve) => {
      this.queue.push({ event, payload, ancestors, target, resolve });
      void this.drain();
    });
  }

  private async drain() {
    if (this.draining || !this.ready) return;
    this.draining = true;
    try {
      while (!this.destroyed && this.queue.length > 0) {
        const item = this.queue.shift()!;
        try {
          await this.processEvent(item);
        } catch (error) {
          console.error(`[TriggerEngine] Event failed: ${item.event}`, error);
        } finally {
          item.resolve();
        }
      }
    } finally {
      this.draining = false;
    }
  }

  private async processEvent(item: QueuedEvent) {
    const rules = await this.getRules();
    if (this.destroyed) return;
    const targetRule = item.target
      ? rules.find((rule) => rule.id === item.target!.ruleId && rule.enabled &&
          ruleIdentity(rule) === item.target!.identity)
      : undefined;
    if (item.target && (!targetRule || !item.target.isCurrent())) return;

    for (const handler of this.globalEventHandlers) {
      try { handler(item.event, item.payload); }
      catch (error) { console.error("[TriggerEngine] Global observer failed:", error); }
    }

    for (const candidate of targetRule ? [targetRule] : rules) {
      if (this.destroyed) return;
      const rule = (await this.getRules()).find((current) => current.id === candidate.id &&
        ruleIdentity(current) === ruleIdentity(candidate));
      if (this.destroyed || !rule || (item.target && !item.target.isCurrent())) continue;
      if (!rule.enabled || rule.event !== item.event || item.ancestors.has(rule.id)) continue;
      const identity = ruleIdentity(rule);
      if (rule.once && this.consumedOnce.has(identity)) continue;
      if (!item.target && !this.matchesRule(rule, item.payload)) continue;
      if (rule.once) this.consumedOnce.add(identity);
      const succeeded = await this.executeAction(rule, item.payload, {
        ancestors: [...item.ancestors, rule.id],
      });
      if (rule.once) {
        if (succeeded) await this.removeRules([rule]);
        else this.consumedOnce.delete(identity);
      }
    }
  }

  private matchesRule(rule: ITriggerRule, payload: unknown): boolean {
    const config = rule.eventConfig ?? {};
    if (rule.event === "event_chat") return matchesChatEvent(config, payload);
    if (!payload || typeof payload !== "object") return true;
    return Object.entries(payload).every(([key, value]) =>
      !config[key] || config[key] === "__all__" || config[key] === value);
  }

  private timerControls() {
    return this.wiredSources.get(EVENT_SOURCE_MAP.timer_reached)?.timerControls;
  }

  getTimerState(ruleId: string): TriggerTimerState | null {
    return this.timerControls()?.getState(ruleId) ?? null;
  }

  async startTimer(ruleId: string): Promise<void> {
    const command = this.nextTimerCommand(ruleId);
    await this.sync();
    if (this.timerCommands.get(ruleId) === command) this.timerControls()?.start(ruleId);
  }

  pauseTimer(ruleId: string): void {
    this.nextTimerCommand(ruleId);
    this.timerControls()?.pause(ruleId);
  }

  async resetTimer(ruleId: string): Promise<void> {
    const command = this.nextTimerCommand(ruleId);
    await this.sync();
    if (this.timerCommands.get(ruleId) === command) this.timerControls()?.reset(ruleId);
  }

  private nextTimerCommand(ruleId: string): number {
    const command = (this.timerCommands.get(ruleId) ?? 0) + 1;
    this.timerCommands.set(ruleId, command);
    return command;
  }

  async getRules(): Promise<ITriggerRule[]> {
    const db = await openDatabase();
    const widgets = await db.widgets.toArray();
    const widget = widgets.find((item) => item.type === WidgetsType.TRIGGERS);
    return (widget as { configuration: { rules: ITriggerRule[] } } | undefined)
      ?.configuration?.rules ?? [];
  }

  private async removeRules(executed: ITriggerRule[]) {
    if (this.destroyed) return;
    try {
      const db = await openDatabase();
      const identities = new Map(executed.map((rule) => [rule.id, ruleIdentity(rule)]));
      const ruleIds = await db.transaction("rw", db.widgets, async () => {
        const widgets = await db.widgets.toArray();
        const widget = widgets.find((item) => item.type === WidgetsType.TRIGGERS);
        if (!widget || !widget.id || this.destroyed) return [];
        const config = (widget as { configuration: { rules: ITriggerRule[] } }).configuration;
        const removedIds = config.rules.filter((rule) => identities.get(rule.id) === ruleIdentity(rule))
          .map((rule) => rule.id);
        if (removedIds.length > 0) {
          const ids = new Set(removedIds);
          await db.widgets.update(widget.id, {
            configuration: { ...config, rules: config.rules.filter((rule) => !ids.has(rule.id)) },
          });
        }
        return removedIds;
      });
      if (ruleIds.length === 0) return;
      this.emitAction("rules_removed", { ruleIds });
      await this.sync();
      await saveSnapshotDatabase();
    } catch (error) {
      console.error("[TriggerEngine] Failed to remove once-rules:", error);
    }
  }

  async executeAction(
    rule: ITriggerRule,
    eventPayload?: unknown,
    eventContext?: TriggerEventContext,
  ): Promise<boolean> {
    const executor = actionRegistry.get(rule.action);
    if (!executor || this.destroyed) return false;
    try {
      await executor(this.Radio, rule.actionConfig ?? {}, {
        emitAction: this.emitAction.bind(this),
        suppressEvent: this.suppressEvent.bind(this),
        eventPayload,
        eventContext: eventContext ?? { ancestors: [rule.id] },
      });
      this.emitAction("rule_executed", { ruleId: rule.id });
      return true;
    } catch (error) {
      console.error(`[TriggerEngine] Action failed: ${rule.action}`, error);
      return false;
    }
  }
}

export default TriggerEngine;
